import { JSX } from 'react';
import { MultiPolygon, Polygon } from 'geojson';

export interface NamedUrl {
    url: string;
    name: string | null;
}

export interface LatLng {
    lat: number;
    lng: number;
}

export interface Tile {
    x: number;
    y: number;
    z: number;
    key: string;
    source: string;
}

export enum ToastType {
    WARNING = 'WARNING',
    SUCCESS = 'SUCCESS',
}

export interface IToast {
    message: string | JSX.Element;
    type: ToastType;
}

export enum ProcessingBuildStatus {
    CREATED = 'CREATED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    DEPRECATED = 'DEPRECATED',
    UPGRADING = 'UPGRADING',
}

export enum MapUploadStatus {
    CLIENT_UPLOAD = 'CLIENT_UPLOAD',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
}

export interface MapEntity {
    _id: string;
    name: string;
    createdBy: string;
}

export interface Geometry {
    geoJson: Polygon | MultiPolygon;
}

export interface MapEntityWithGeometry extends MapEntity, Geometry {}

export interface ProcessingBuildRequestProps {
    zoneId: string;
    name: string;
    zoom: number;
}

export interface ZoneListItem extends MapEntity {
    createdAt: string;
    area: number;
}

export interface Zone extends ZoneListItem, MapEntityWithGeometry {}

export enum VersionValidity {
    NOT_VALID = 0,
    VALID = 1,
}

export interface ProcessingBuildListItem extends MapEntity {
    status: ProcessingBuildStatus;
    processedMap?: ProcessedMapListItem;
    createdAt: string;
    updatedAt?: string;
    finishedAt?: string;
    models: any;
    error?: string;
    zoom: number;
}

export interface ProcessingBuild extends Omit<ProcessingBuildListItem, 'status'>, MapEntityWithGeometry {}

export interface ProcessedMapListItem extends MapEntity {
    createdAt: string;
    buildId: string;
    zoneId: string;
    models: string[];
    zoom: number;
    uploadKey: string;
}

export interface ProcessedMap extends ProcessedMapListItem, MapEntityWithGeometry {}

export interface MapUploadListItem extends Geometry {
    _id: string;
    name?: string;
    upload_id: number;
    zoom: number;
    directory: string;
    source: string;
    status: MapUploadStatus;
    createdBy: string;
    createdAt: string;
}

export interface ObjectId {
    $oid: string;
}

export interface Device {
    deviceId?: string;
    matrixCode?: string;
    baseImageS3Link?: string;
    status?: string;
    commitHash?: string;
    deviceSerial?: number;
    zymkeySerial?: string;
    rpiSerial?: string;
    username?: string;
    hostname?: string;
    sshPort?: number;
    accessPointSSID?: string;
    vpnProfile?: string;
    vpnIPAddress?: string;
    vpnOnlineAt?: string;
    vpnPubkey?: string;
    bundleDevicePubkey?: string;
    bundleCloudPubkey?: string;
    bundleCloudPrivkey?: string;
    mapS3Link?: string;
    secureBootPrivkey?: string;
    zymkeyPubkey?: string;
    deviceSSHPubkey?: string;
    deviceSSHPubkeyType?: string;
    deviceSSHPubkeyHash?: string;
    systemVersion?: string;
    hardwareConfiguration?: string;
    createdAt?: string;
    updatedAt?: string;
    organizationDetails?: Organization;
    configurationDetails?: SystemConfiguration;
    checksDetails?: CheckDetails[];
    calibrationFiles?: CalibrationFilesMap;
    routeCard?: DeviceRouteCardSummary | null;
}

export interface DeviceRouteCardSummary {
    id: string;
    status: RouteCardStatus;
    pdfUrl: string | null;
}

export type CalibrationFilesMap = Partial<Record<CalibrationFileType, CalibrationFile>>;

export enum CalibrationFileType {
    DAY = 'day',
    THERMAL = 'thermal',
}

export interface CalibrationFile {
    Key: CalibrationFileType;
    Content: string;
    UpdatedAt: string;
}

export interface CheckDetails {
    id: string;
    Key: string;
    Url: string;
    IsFinal: boolean;
    CreatedAt: string;
}

export interface DeviceSecrets {
    userPassword: string;
    accessPointPassword: string;
}

export interface CreateDeviceForm {
    organizationId?: string;
    systemVersion: string;
    hardwareConfiguration: string;
}

export interface UpsertOrganizationDialogProps {
    open: boolean;
    onClose: () => void;
    initialValues: Organization;
}

export interface Organization {
    id?: string | null;
    name?: string;
    labelColor?: string;
    systemVersion?: string | null;
}

export interface DeviceWithSticks extends Device {
    activationSticks: ActivationStick[];
}

export interface OnboardDevicesPagination {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

export type OnboardDevicesSortOrder = 'asc' | 'desc';

export type OnboardDevicesField =
    | 'deviceId'
    | 'deviceSerial'
    | 'matrixCode'
    | 'status'
    | 'baseImageS3Link'
    | 'commitHash'
    | 'zymkeySerial'
    | 'rpiSerial'
    | 'username'
    | 'hostname'
    | 'sshPort'
    | 'accessPointSSID'
    | 'vpnProfile'
    | 'vpnIPAddress'
    | 'vpnPubkey'
    | 'bundleDevicePubkey'
    | 'bundleCloudPubkey'
    | 'mapS3Link'
    | 'zymkeyPubkey'
    | 'deviceSSHPubkey'
    | 'deviceSSHPubkeyType'
    | 'deviceSSHPubkeyHash'
    | 'systemVersion'
    | 'hardwareConfiguration'
    | 'vpnOnlineAt'
    | 'createdAt'
    | 'updatedAt'
    | 'organizationId';

export type OnboardDevicesSortBy = OnboardDevicesField;

export type OnboardDevicesFilterOperator =
    | 'equals'
    | 'contains'
    | 'notEquals'
    | '='
    | '!='
    | 'isAnyOf'
    | 'isNull'
    | 'isNotNull';

export interface OnboardDevicesFilter {
    field: OnboardDevicesField;
    operator: OnboardDevicesFilterOperator;
    value?: string | number | Array<string | number>;
}

export type OnboardDevicesFilters = OnboardDevicesFilter[];

export interface OnboardDevicesQueryParams {
    page: number;
    pageSize: number;
    search?: string;
    sortBy?: OnboardDevicesSortBy;
    sortOrder?: OnboardDevicesSortOrder;
    filters?: OnboardDevicesFilters;
}

export interface OnboardDevicesResponse {
    items: Device[];
    pagination: OnboardDevicesPagination;
}

export interface ActivationStick {
    activationStickId: string;
    deviceId: string;
    deviceSerial: number;
    partitionUUID: string;
    rpiSerial: string;
    bundleS3Link: string;
    systemVersion: string;
}

export interface SystemConfiguration {
    _id: ObjectId;
    systemVersion: string;
    configuration: {
        applicationVersion: string;
        mapVersion: string;
        containerVersion: string;
        osVersion: string | null;
        odometryOn: boolean | null;
    };
}

export enum UserRoles {
    ADMINS = 'admins',
    FARM = 'farm',
}

export interface TileSourcesResult {
    presigned_url: string;
    geoJson: Polygon | MultiPolygon | Array<Polygon | MultiPolygon>;
}

export interface ShapeHistoryItem {
    name: string;
    geoJson: Polygon | MultiPolygon;
}

export interface PostOnboardDeviceLogsRequest {
    startDate?: string | null;
    endDate?: string | null;
}

export interface LogStatus {
    name: string;
    type: 'application' | 'system';
    status: 'in progress' | 'completed' | 'failed';
    totalFilesCount: number;
    uploadedFilesCount: number;
    failedFilesCount: number;
    details?: string[] | null;
}

export interface OnboardDeviceLogsUploadStatus {
    requestId: string;
    logsStatuses: LogStatus[];
    status: 'in progress' | 'finished';
    details?: string | null;
}

export interface OnboardDeviceOnlinePeerHuman {
    rx?: string;
    tx?: string;
    last_handshake?: string;
    last_handshake_at?: string;
    online_duration?: string;
    total_rx?: string;
    total_tx?: string;
    total_transfer?: string;
}

export interface OnboardDeviceOnlinePeer {
    deviceId?: string;
    pubkey?: string;
    name?: string | null;
    endpoint?: string | null;
    allowed_ips?: string[];
    rx_bytes?: number;
    tx_bytes?: number;
    last_handshake_secs?: number;
    is_online?: boolean;
    online_since?: string | null;
    online_secs?: number;
    sessions_count?: number;
    total_rx?: number;
    total_tx?: number;
    total_transfer?: number;
    human?: OnboardDeviceOnlinePeerHuman;
}

export interface OnboardDevicesOnlinePeersResponse {
    vpn_server_main: OnboardDeviceOnlinePeer[];
    vpn_server_secondary: OnboardDeviceOnlinePeer[];
}

export type OnboardDeviceConnectionStatus = 'online';

export interface OnboardDeviceOnlineStatusWsPayload {
    type: 'onboard_device_online_status';
    deviceId: string;
    pubkey?: string;
    status: 'online' | 'offline';
    serverType?: string;
    timestamp?: string;
}

export interface OnboardDeviceHardwareConfiguration {
    id?: string;
    name: string;
    documentation: NamedUrl | null;
    createdAt: string;
    createdBy: string;
    updatedAt: string | null;
    updatedBy: string | null;
    comment: string | null;
}

export interface OnboardDeviceHardwareConfigurationRequestBody {
    name: string | null;
    documentation: NamedUrl | null;
    comment: string | null;
}

export type RouteCardStatus = 'draft' | 'completed';

export interface RouteCardOperation {
    operationId: string;
    name: string;
    section: string | null;
    order: number;
    requiresFirmwareValue: boolean;
    expectedFirmwareValue: string | null;
    completed: boolean;
    completedAt: string | null;
    completedBy: string | null;
    firmwareValue: string | null;
    notes: string | null;
}

export interface RouteCard {
    id: string;
    deviceId: string;
    templateId: string;
    templateVersion: number;
    templateName: string | null;
    instructions: string;
    status: RouteCardStatus;
    operations: RouteCardOperation[];
    pdfUrl: string | null;
    createdAt: string;
    updatedAt: string | null;
    createdBy: string;
    updatedBy: string | null;
}

export interface CreateRouteCardRequestBody {
    deviceId: string;
}

export interface RouteCardOperationUpdate {
    operationId: string;
    completed: boolean;
    firmwareValue?: string | null;
}

export interface RouteCardOperationsUpdateRequestBody {
    operations: RouteCardOperationUpdate[];
}

export interface RouteCardOperationsUpdateDevicePatch {
    deviceId: string;
    status: string;
    updatedAt: string | null;
}

export interface RouteCardOperationsUpdateResponse {
    routeCard: RouteCard;
    device: RouteCardOperationsUpdateDevicePatch | null;
}

export interface RouteCardsPagination {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

export interface RouteCardsResponse {
    items: RouteCard[];
    pagination: RouteCardsPagination;
}

// Backend only supports plain equality filters on these two fields (no
// operator/JSON filters array like onboard-devices) - see
// functions/RouteCard/getRouteCards in mapnav-cloud-server.
export type RouteCardsSortBy = 'deviceId' | 'status' | 'createdAt' | 'updatedAt';

export interface RouteCardsQueryParams {
    page: number;
    pageSize: number;
    sortBy?: RouteCardsSortBy;
    sortOrder?: OnboardDevicesSortOrder;
    deviceId?: string;
    status?: RouteCardStatus;
}
